import React, { useEffect, useRef, useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useSavedViews } from "@features/sales-v3/hooks";
import { type SavedView } from "@features/sales-v3/hooks/use-saved-views-queries";
import { TertiaryButton } from "@shared/buttons";

import { SavedViewsMenuLoadingRow } from "./saved-views-menu-loading-row";

interface UpdateSavedViewsProps {
  savedView: SavedView;
  isUpdating: boolean;
  setIsUpdating(value: boolean): void;
}

export const UpdateSavedViews = ({
  savedView,
  isUpdating,
  setIsUpdating,
}: UpdateSavedViewsProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(savedView.name);
  const { updateSavedViewName } = useSavedViews();
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const updateSavedView = async () => {
    prettyLogEventPage({
      domain: "Saved Views",
      action: "onClick",
      additionalActionInfo: "Update Saved View Name",
      data: {
        pageContext: { savedView },
      },
    });

    try {
      setIsUpdateLoading(true);
      await updateSavedViewName(savedView.id, localValue);
      setIsUpdating(false);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  useEffect(() => {
    if (isUpdating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isUpdating]);

  if (!isUpdating) {
    return null;
  }

  return (
    <div className="text-xs w-full h-full flex items-center bg-inherit z-1">
      <SavedViewsMenuLoadingRow isLoading={isUpdateLoading} text="Updating...">
        <div className="border-b border-cosmic flex items-center w-full">
          <input
            ref={inputRef}
            className="flex-1 text-sm h-full bg-inherit font-normal w-full"
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
          />
          <TertiaryButton
            className="hover:bg-transparent"
            onClick={updateSavedView}
          >
            SAVE
          </TertiaryButton>
        </div>
      </SavedViewsMenuLoadingRow>
    </div>
  );
};
